import React, { useState, useCallback } from 'react';
import { 
  Upload, Facebook, Globe, FileSpreadsheet, CheckCircle2, 
  AlertCircle, X, ArrowRight, Sparkles, Users, Zap,
  ChevronRight, Download
} from 'lucide-react';
import { Button } from './ui/button';
import * as api from '../services/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Simple CSV parser
const parseCSV = (text) => {
  const lines = text.split('\n').filter(line => line.trim());
  if (lines.length === 0) return { headers: [], rows: [] };
  
  // Parse header
  const headers = lines[0].split(',').map(h => h.replace(/"/g, '').trim().toLowerCase());
  
  // Parse rows
  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const values = [];
    let current = '';
    let inQuotes = false;
    
    for (const char of lines[i]) {
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        values.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    values.push(current.trim());
    
    if (values.length > 0 && values.some(v => v)) {
      const row = {};
      headers.forEach((h, idx) => {
        row[h] = values[idx] || '';
      });
      rows.push(row);
    }
  }
  
  return { headers, rows };
};

// Auto-detect column mapping
const detectColumnMapping = (headers) => {
  const mapping = {
    name: null,
    email: null,
    phone: null,
    address: null,
    zone: null,
    notes: null
  };
  
  const patterns = {
    name: ['name', 'customer name', 'full name', 'customer', 'client'],
    email: ['email', 'e-mail', 'customer email', 'mail'],
    phone: ['phone', 'telephone', 'mobile', 'customer phone', 'tel', 'contact'],
    address: ['address', 'customer address', 'street', 'location'],
    zone: ['zone', 'area', 'region', 'territory', 'round'],
    notes: ['notes', 'customer notes', 'comments', 'description']
  };
  
  headers.forEach(header => {
    const h = header.toLowerCase();
    for (const [field, keywords] of Object.entries(patterns)) {
      if (!mapping[field] && keywords.some(k => h.includes(k))) {
        mapping[field] = header;
      }
    }
  });
  
  return mapping;
};

// Bulk Import Section
const BulkImportSection = ({ onImportComplete }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState(null);
  const [columnMapping, setColumnMapping] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [error, setError] = useState(null);
  const [squeegeeMode, setSqueegeeMode] = useState(false);
  const [squeegeePreview, setSqueegeePreview] = useState(null);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.name.endsWith('.csv')) {
      processFile(droppedFile);
    } else {
      setError('Please drop a CSV file');
    }
  }, []);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      processFile(selectedFile);
    }
  };

  const processFile = async (file) => {
    setFile(file);
    setError(null);
    setImportResult(null);
    setSqueegeeMode(false);
    setSqueegeePreview(null);

    try {
      const text = await file.text();

      // Squeegee full export (one row per job, multi-line addresses) —
      // the browser parser can't handle it; the backend does it properly.
      if (text.slice(0, 2000).includes('Job - Job Ref')) {
        setSqueegeeMode(true);
        const token = api.getToken();
        const fd = new FormData();
        fd.append('file', file);
        const res = await fetch(`${API_URL}/api/import/squeegee/preview`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: fd
        });
        if (!res.ok) {
          const d = await res.json().catch(() => ({}));
          throw new Error(d.detail || 'Could not analyse Squeegee file');
        }
        setSqueegeePreview(await res.json());
        return;
      }

      const { headers, rows } = parseCSV(text);
      if (rows.length === 0) {
        setError('No data found in CSV');
        return;
      }
      setParsedData({ headers, rows });
      setColumnMapping(detectColumnMapping(headers));
    } catch (err) {
      setError(err.message || 'Could not read CSV file');
    }
  };

  const handleImport = async () => {
    // Squeegee mode — server handles grouping, jobs and geocoding
    if (squeegeeMode) {
      setImporting(true);
      setError(null);
      try {
        const token = api.getToken();
        const fd = new FormData();
        fd.append('file', file);
        const res = await fetch(`${API_URL}/api/import/squeegee`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: fd
        });
        if (!res.ok) {
          const d = await res.json().catch(() => ({}));
          throw new Error(d.detail || 'Squeegee import failed');
        }
        const data = await res.json();
        setImportResult({
          success: true,
          created: data.created_customers,
          jobsCreated: data.created_jobs,
          errors: data.error_count || 0
        });
        setSqueegeePreview(null);
        if (onImportComplete) onImportComplete();
      } catch (err) {
        setError(err.message);
      } finally {
        setImporting(false);
      }
      return;
    }

    if (!parsedData || !columnMapping.name) {
      setError('Please make sure customer name column is mapped');
      return;
    }

    setImporting(true);
    setError(null);

    try {
      const customers = parsedData.rows.map(row => ({
        name: row[columnMapping.name] || 'Unknown',
        email: columnMapping.email ? row[columnMapping.email] : null,
        phone: columnMapping.phone ? row[columnMapping.phone] : null,
        address: columnMapping.address ? row[columnMapping.address] : null,
        zone: columnMapping.zone ? row[columnMapping.zone] : null,
        notes: columnMapping.notes ? row[columnMapping.notes] : null
      })).filter(c => c.name && c.name !== 'Unknown');

      // Split into batches of 100
      const batchSize = 100;
      let totalCreated = 0;
      let totalErrors = 0;

      for (let i = 0; i < customers.length; i += batchSize) {
        const batch = customers.slice(i, i + batchSize);
        
        const token = api.getToken();
        const res = await fetch(`${API_URL}/api/customers/bulk`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ customers: batch })
        });

        if (res.ok) {
          const data = await res.json();
          totalCreated += data.created_count || batch.length;
          totalErrors += data.error_count || 0;
        }
      }

      setImportResult({
        success: true,
        created: totalCreated,
        errors: totalErrors
      });

      if (onImportComplete) {
        onImportComplete();
      }
    } catch (err) {
      setError('Import failed. Please try again.');
    } finally {
      setImporting(false);
    }
  };

  const resetImport = () => {
    setFile(null);
    setParsedData(null);
    setColumnMapping(null);
    setImportResult(null);
    setError(null);
    setSqueegeeMode(false);
    setSqueegeePreview(null);
  };

  return (
    <div className="bg-white rounded-2xl border border-zinc-200 overflow-hidden">
      <div className="p-4 border-b border-zinc-100">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
            <Upload size={20} className="text-blue-600" />
          </div>
          <div>
            <h3 className="font-semibold text-zinc-900">Import Customers</h3>
            <p className="text-sm text-zinc-500">Add customers from a CSV file</p>
          </div>
        </div>
      </div>

      <div className="p-4">
        {!parsedData && !squeegeePreview && !importResult && (
          <>
            {/* Drop zone */}
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`
                relative border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer
                ${isDragging 
                  ? 'border-emerald-500 bg-emerald-50' 
                  : 'border-zinc-200 hover:border-zinc-300 hover:bg-zinc-50'
                }
              `}
              onClick={() => document.getElementById('csv-upload').click()}
            >
              <input
                id="csv-upload"
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                className="hidden"
              />
              
              <div className="w-16 h-16 bg-zinc-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <FileSpreadsheet size={28} className="text-zinc-400" />
              </div>
              
              <p className="font-medium text-zinc-900 mb-1">
                {isDragging ? 'Drop your file here!' : 'Drop CSV file here'}
              </p>
              <p className="text-sm text-zinc-500">or click to browse</p>
              
              <div className="mt-4 flex items-center justify-center gap-2 text-xs text-zinc-400">
                <span>Works with exports from any CRM</span>
              </div>
            </div>

            {error && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
                <AlertCircle size={16} />
                {error}
              </div>
            )}
          </>
        )}

        {squeegeePreview && !importResult && (
          <div className="space-y-4" data-testid="bulk-squeegee-preview">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
              <p className="font-semibold text-emerald-900">Squeegee export detected ✓</p>
              <p className="text-sm text-emerald-700 mt-1">
                {squeegeePreview.customers} customers and {squeegeePreview.jobs} jobs will be imported with prices, frequencies, due dates, rounds and assignees. Map pins are added automatically.
              </p>
            </div>
            {squeegeePreview.multi_job_customers?.length > 0 && (
              <div className="bg-sky-50 border border-sky-200 rounded-xl p-3">
                {squeegeePreview.multi_job_customers.map((m, i) => (
                  <p key={i} className="text-sm text-sky-700">{m.name} — {m.jobs} jobs ({m.addresses.join(' • ')})</p>
                ))}
              </div>
            )}
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
                <AlertCircle size={16} />
                {error}
              </div>
            )}
            <div className="flex gap-3">
              <Button variant="outline" onClick={resetImport} className="flex-1">Cancel</Button>
              <Button
                onClick={handleImport}
                disabled={importing}
                data-testid="bulk-squeegee-import-btn"
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
              >
                {importing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                    Importing...
                  </>
                ) : (
                  <>Import {squeegeePreview.customers} Customers & {squeegeePreview.jobs} Jobs</>
                )}
              </Button>
            </div>
          </div>
        )}

        {parsedData && !importResult && (
          <div className="space-y-4">
            {/* Preview */}
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <Users size={24} className="text-emerald-600" />
                </div>
                <div>
                  <p className="font-semibold text-emerald-900 text-lg">
                    {parsedData.rows.length} customers found!
                  </p>
                  <p className="text-sm text-emerald-700">Ready to import</p>
                </div>
              </div>
            </div>

            {/* Column mapping preview */}
            <div className="bg-zinc-50 rounded-xl p-4">
              <p className="text-sm font-medium text-zinc-700 mb-3">Auto-detected columns:</p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                {Object.entries(columnMapping).map(([field, column]) => (
                  <div key={field} className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${column ? 'bg-emerald-500' : 'bg-zinc-300'}`} />
                    <span className="text-zinc-600 capitalize">{field}:</span>
                    <span className={column ? 'text-zinc-900' : 'text-zinc-400'}>
                      {column || 'Not found'}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Sample preview */}
            <div className="border border-zinc-200 rounded-xl overflow-hidden">
              <div className="bg-zinc-50 px-3 py-2 border-b border-zinc-200">
                <p className="text-xs font-medium text-zinc-500">PREVIEW (first 3 customers)</p>
              </div>
              <div className="divide-y divide-zinc-100">
                {parsedData.rows.slice(0, 3).map((row, i) => (
                  <div key={i} className="px-3 py-2 text-sm">
                    <p className="font-medium text-zinc-900">
                      {row[columnMapping.name] || 'Unknown'}
                    </p>
                    <p className="text-zinc-500 text-xs">
                      {[
                        columnMapping.email && row[columnMapping.email],
                        columnMapping.phone && row[columnMapping.phone]
                      ].filter(Boolean).join(' • ') || 'No contact info'}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
                <AlertCircle size={16} />
                {error}
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={resetImport}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleImport}
                disabled={importing || !columnMapping.name}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
              >
                {importing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                    Importing...
                  </>
                ) : (
                  <>
                    Import All
                    <ArrowRight size={16} className="ml-2" />
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {importResult && (
          <div className="text-center py-6">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 size={32} className="text-emerald-600" />
            </div>
            <h3 className="text-xl font-bold text-zinc-900 mb-1">
              Import Complete! 🎉
            </h3>
            <p className="text-zinc-600">
              {importResult.created} customers added successfully
              {importResult.jobsCreated ? ` with ${importResult.jobsCreated} jobs` : ''}
            </p>
            {importResult.errors > 0 && (
              <p className="text-amber-600 text-sm mt-1">
                {importResult.errors} couldn't be imported
              </p>
            )}
            <Button
              onClick={resetImport}
              variant="outline"
              className="mt-4"
            >
              Import More
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

// Ads Integration Section
const AdsIntegrationSection = ({ onNavigateToApiDocs }) => {
  return (
    <div className="bg-white rounded-2xl border border-zinc-200 overflow-hidden">
      <div className="p-4 border-b border-zinc-100">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
            <Zap size={20} className="text-purple-600" />
          </div>
          <div>
            <h3 className="font-semibold text-zinc-900">Lead Ads Integration</h3>
            <p className="text-sm text-zinc-500">Auto-add leads from your ads</p>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-3">
        {/* Facebook */}
        <button 
          onClick={onNavigateToApiDocs}
          className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 rounded-xl border border-blue-100 transition-colors group"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <Facebook size={20} className="text-white" />
            </div>
            <div className="text-left">
              <p className="font-medium text-zinc-900">Facebook Lead Ads</p>
              <p className="text-xs text-zinc-500">New leads → Customers automatically</p>
            </div>
          </div>
          <ChevronRight size={20} className="text-zinc-400 group-hover:text-zinc-600 transition-colors" />
        </button>

        {/* Google */}
        <button 
          className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-red-50 to-orange-50 rounded-xl border border-red-100 opacity-60 cursor-not-allowed"
          disabled
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-red-500 via-yellow-500 to-green-500 rounded-lg flex items-center justify-center">
              <Globe size={20} className="text-white" />
            </div>
            <div className="text-left">
              <p className="font-medium text-zinc-900">Google Lead Forms</p>
              <p className="text-xs text-zinc-500">Coming soon</p>
            </div>
          </div>
          <span className="px-2 py-1 bg-zinc-200 text-zinc-600 text-xs rounded-full">Soon</span>
        </button>

        {/* Info box */}
        <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-xs text-amber-800">
            <strong>How it works:</strong> When someone fills out your Facebook ad form, 
            they're automatically added as a new customer here. No manual entry needed!
          </p>
        </div>
      </div>
    </div>
  );
};

// Main export - combined section
const CustomerImportSection = ({ onImportComplete, onNavigateToApiDocs }) => {
  return (
    <div className="px-4 py-6 space-y-4 border-t border-zinc-200 bg-zinc-50">
      <div className="flex items-center gap-2 mb-2">
        <Sparkles size={18} className="text-emerald-500" />
        <h2 className="font-semibold text-zinc-900">Grow Your Customer List</h2>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <BulkImportSection onImportComplete={onImportComplete} />
        <AdsIntegrationSection onNavigateToApiDocs={onNavigateToApiDocs} />
      </div>
    </div>
  );
};

export default CustomerImportSection;
export { BulkImportSection, AdsIntegrationSection };
